import { useLeadsColdCandidatesRetrieve, type LeadColdCandidateList } from '@gta/api-client'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Lock, RefreshCw } from 'lucide-react'

import { EmptyState, ErrorState } from '@/components/console/states'
import { SectionCard } from '@/components/console/section-card'
import { PageContainer } from '@/components/layout/page-container'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessageOf, unwrapResponse } from '@/features/leads/api-utils'
import { LeadRecallDialog } from '@/features/leads/lead-recall-dialog'
import { silentHoursText } from '@/features/leads/lead-pool'
import { useMetaEnums } from '@/features/leads/use-meta-enums'
import { formatDateTime } from '@/lib/format'
import { useRoleAccess } from '@/lib/role-access'

/**
 * /leads/cold —— **待跟进(冷候选)**(★feat-008 W11;lead-dispatch-design §6.3)。
 *
 * 口径 = **冷-A「客户在等我们回」**:线索关联会话的最后一条消息是**入站**且距今超过
 * `settings.LEAD_COLD_THRESHOLD_HOURS`(James 08-30 亲裁 48h)。
 * 冷-B(销售跟了、客户没回)**只在顶部分布计数里可见**,不进清单也不给动作 ——
 * 收回它等于惩罚跟进过的人(实测 >7 天的冷-B 有 19,827 条、冷-A 只有 908 条)。
 *
 * ★ **空态文案是本页最要紧的一处纪律**(LD-23):DM→Lead 管道开闸前,线索与会话的连接
 * (salesmartly 身份行)只有个位数 ⇒ 本清单近乎恒空。那时必须说「尚无关联会话的线索」,
 * **不得**说「没有待跟进线索」——后者是在报一个假的好消息。判据取 summary 的
 * `leads_without_session`:它 > 0 而 `cold_a` = 0,就是「算不出来」而不是「都跟上了」。
 *
 * ★ **没有「一键收回全部」**(LD-24):收回一条一条来,每条必填理由。存量里 80.6% 的
 * 会话已静默 >30 天,那个按钮的第一次点击就是一次事故。
 *
 * 角色档:主管及以上(LD-9)。前端在这里画一张引导页而不是 redirect ——
 * 静默改 URL 会让人以为自己点错了链接(与 `_authed` 的模块闸同一条纪律)。
 */
export const Route = createFileRoute('/_authed/leads/cold')({
  component: LeadColdPage,
})

function ManagerOnlyNotice() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center px-6 py-12">
      <div className="console-card flex w-full max-w-lg flex-col items-center gap-3 px-8 py-10 text-center">
        <span className="inline-flex size-11 items-center justify-center rounded-md bg-console-hover text-console-muted">
          <Lock className="size-6" />
        </span>
        <p className="text-lg font-medium text-console-text">该操作需要主管及以上权限</p>
        <p className="text-sm text-console-muted">
          待跟进清单是收回线索的工作台,按线索分配策略只对主管及以上开放。
        </p>
        <div className="mt-2">
          <Button asChild>
            <Link to="/leads" search={{}}>
              回到线索列表
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

function SummaryTile({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-console-border px-3 py-2">
      <span className="text-xs text-console-muted">{label}</span>
      <span className="text-lg font-medium text-console-text">{value}</span>
      {hint && <span className="text-xs text-console-subtle">{hint}</span>}
    </div>
  )
}

function LeadColdPage() {
  const { isManager, isLoading: roleLoading } = useRoleAccess()
  const meta = useMetaEnums()
  // 角色未确定前不发请求:sales 打这个端点必然 403,预先打一次只会污染控制台。
  const query = useLeadsColdCandidatesRetrieve(undefined, { query: { enabled: isManager } })
  const data = unwrapResponse<LeadColdCandidateList>(query.data)

  if (!roleLoading && !isManager) {
    return (
      <PageContainer title="待跟进" breadcrumbs={[{ label: '线索管理', to: '/leads' }, { label: '待跟进' }]}>
        <ManagerOnlyNotice />
      </PageContainer>
    )
  }

  const summary = data?.summary
  const rows = data?.results ?? []
  // ★LD-23 的判据:算不出来(没有关联会话)≠ 都跟上了。两种空态说两句不同的话。
  const nothingComputable = (summary?.cold_a ?? 0) === 0 && (summary?.leads_without_session ?? 0) > 0

  return (
    <PageContainer
      title="待跟进"
      breadcrumbs={[{ label: '线索管理', to: '/leads' }, { label: '待跟进' }]}
      description={
        summary
          ? `冷口径:客户最后一条消息是入站且距今超过 ${String(summary.threshold_hours)} 小时(客户在等我们回)。`
          : '冷口径:客户最后一条消息是入站且超过阈值(客户在等我们回)。'
      }
      actions={
        <>
          <Button variant="outline" asChild>
            <Link to="/leads" search={{}}>
              <ArrowLeft />
              回到线索列表
            </Link>
          </Button>
          <Button
            variant="outline"
            disabled={query.isFetching}
            onClick={() => {
              void query.refetch()
            }}
          >
            <RefreshCw className={query.isFetching ? 'animate-spin' : undefined} />
            刷新
          </Button>
        </>
      }
    >
      {query.isError ? (
        <ErrorState
          title="待跟进清单加载失败"
          message={errorMessageOf(query.error, '请稍后重试')}
          onRetry={() => {
            void query.refetch()
          }}
        />
      ) : (
        <>
          {summary && (
            <SectionCard title="冷度分布" description="冷-B「销售跟了、客户没回」只在这里以计数形态可见,不产出可动作清单。">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                <SummaryTile label="待跟进(冷-A)" value={summary.cold_a} hint="客户在等回复" />
                <SummaryTile label="客户未回(冷-B)" value={summary.cold_b} hint="只可见,不收回" />
                <SummaryTile label="机器人结尾" value={summary.cold_bot} hint="既不催也不罚" />
                <SummaryTile label="阈值内" value={summary.within_threshold} hint="正常跟进中" />
                <SummaryTile label="无关联会话" value={summary.leads_without_session} hint="算不出冷度" />
                <SummaryTile label="考察面" value={summary.assigned_open_leads} hint="有归属且未落终态" />
              </div>
            </SectionCard>
          )}

          <div className="console-card overflow-hidden" aria-busy={query.isFetching}>
            {query.isLoading ? (
              <div className="flex flex-col gap-2 px-4 py-3.5">
                {Array.from({ length: 5 }, (_, index) => (
                  <Skeleton key={index} className="h-8 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <EmptyState
                title={nothingComputable ? '尚无关联会话的线索' : '没有待跟进的线索'}
                description={
                  nothingComputable
                    ? `当前有 ${String(summary?.leads_without_session ?? 0)} 条已派发线索还没有关联到 SalesSmartly 会话,冷度算不出来 —— 这不是「都跟上了」。会话关联由 DM→Lead 管道建立,管道开闸后本页才会有数据。`
                    : '所有已派发线索的客户消息都在阈值内,没有人在等回复。'
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-console-divider text-xs text-console-muted">
                    <tr>
                      <th className="px-4 py-2 text-left font-normal">线索</th>
                      <th className="px-4 py-2 text-left font-normal">负责人</th>
                      <th className="px-4 py-2 text-left font-normal">客户最后消息</th>
                      <th className="px-4 py-2 text-left font-normal">已等待</th>
                      <th className="px-4 py-2 text-right font-normal">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.lead_resource_id} className="border-b border-console-divider last:border-b-0">
                        <td className="px-4 py-2">
                          <Link
                            to="/leads/$leadId"
                            params={{ leadId: row.lead_resource_id }}
                            search={{ tab: 'activities' }}
                            className="console-link-action font-mono text-xs"
                          >
                            {row.lead_resource_id}
                          </Link>
                        </td>
                        <td className="px-4 py-2">
                          {meta.employees.find((item) => item.resource_id === row.owner_employee_resource_id)
                            ?.display_name ?? row.owner_employee_resource_id}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(row.last_message_time)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-status-warning">
                          {silentHoursText(row.silent_hours)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <LeadRecallDialog
                            leadResourceId={row.lead_resource_id}
                            ownerDisplayName={
                              meta.employees.find((item) => item.resource_id === row.owner_employee_resource_id)
                                ?.display_name
                            }
                            size="sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </PageContainer>
  )
}
